export type FavListItem = {
  id: string;
  productId: string;
  wishlistId: string;
  quantity: number;
};

export type FavListCollection = {
  id: string;
  listName: string;
  date: {
    from: string | undefined;
    to: string | undefined;
  };
  products: FavListItem[];
  customerId: string;
};

type ApiWishlistItem = {
  id: string;
  product_id: string;
  wishlist_id: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  quantity: number;
};

type ApiWishlist = {
  id: string;
  customer_id: string;
  name: string;
  dateRange: {
    to: string;
    from: string;
  };
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  items: ApiWishlistItem[];
};

type CreateWishlistResponse = {
  wishlist: FavListCollection;
};

const transformApiWishlistToState = (apiWishlist: ApiWishlist): FavListCollection => {
  return {
    id: apiWishlist.id,
    listName: apiWishlist.name,
    date: {
      from: apiWishlist.dateRange?.from,
      to: apiWishlist.dateRange?.to,
    },
    products: apiWishlist.items.map((item) => ({
      id: item.id,
      productId: item.product_id,
      wishlistId: item.wishlist_id,
      quantity: item.quantity || 1,
    })),
    customerId: apiWishlist.customer_id,
  };
};

const makeApiCall = async (url: string, options: RequestInit = {}) => {
  const baseUrl = process.env.NEXT_PUBLIC_MEDUSA_BACKEND_URL || "http://localhost:9000";
  const apiKey = process.env.NEXT_PUBLIC_MEDUSA_PUBLISHABLE_KEY;

  if (!apiKey) {
    throw new Error("Missing NEXT_PUBLIC_MEDUSA_PUBLISHABLE_KEY environment variable");
  }

  const response = await fetch(`${baseUrl}${url}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      "x-publishable-api-key": apiKey,
      ...options.headers,
    },
    credentials: "include",
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => "Unknown error");
    throw new Error(`API Error (${response.status}): ${errorText}`);
  }

  return response;
};

export const wishlistApi = {
  fetchLists: async (customerId: string): Promise<FavListCollection[]> => {
    const response = await makeApiCall(`/store/wishlist/fetchall/${customerId}`);
    const data = await response.json();
    const apiWishlists: ApiWishlist[] = data.wishlist;
    const safeApiWishlists = Array.isArray(apiWishlists) ? apiWishlists : [];
    return safeApiWishlists.map((apiWishlist) => {
      const list = transformApiWishlistToState(apiWishlist);
      return {
        ...list,
        products: list.products.map((p) => ({ ...p })),
      };
    });
  },

  createList: async (params: {
    listName: string;
    customerId: string;
    productId?: string;
    dateRange?: { from: string; to: string };
  }): Promise<FavListCollection> => {
    const requestBody = {
      customer_id: params.customerId,
      name: params.listName,
      ...(params.productId && { product_id: params.productId }),
      ...(params.dateRange && { date_range: params.dateRange }),
    };

    const response = await makeApiCall("/store/wishlist/create", {
      method: "POST",
      body: JSON.stringify(requestBody),
    });

    const data: CreateWishlistResponse = await response.json();
    return data.wishlist;
  },

  addToList: async (wishlistId: string, productId: string): Promise<FavListCollection> => {
    const response = await makeApiCall(`/store/wishlist/addproduct/${wishlistId}`, {
      method: "POST",
      body: JSON.stringify({
        product_id: productId,
      }),
    });

    const data: CreateWishlistResponse = await response.json();
    return data.wishlist;
  },

  updateQuantity: async (wishlistItemId: string, quantity: number): Promise<{ wishlistItem: FavListItem }> => {
    const response = await makeApiCall(`/store/wishlist/updatequantity/${wishlistItemId}`, {
      method: "POST",
      body: JSON.stringify({
        quantity: quantity,
      }),
    });

    return await response.json();
  },

  removeFromList: async (wishlistItemId: string): Promise<void> => {
    await makeApiCall(`/store/wishlist/deleteproduct/${wishlistItemId}`, {
      method: "DELETE",
    });
  },

  deleteList: async (wishlistId: string): Promise<void> => {
    await makeApiCall(`/store/wishlist/delete/${wishlistId}`, {
      method: "DELETE",
    });
  },

  renameList: async (wishlistId: string, newName: string): Promise<FavListCollection> => {
    const response = await makeApiCall(`/store/wishlist/updatename/${wishlistId}`, {
      method: "POST",
      body: JSON.stringify({
        name: newName,
      }),
    });

    const data: CreateWishlistResponse = await response.json();
    return data.wishlist;
  },
};
